import type { createMemoryRouter } from 'react-router-dom'
import { DISCARD_QUESTION } from '../components/unsaved'
import { tellShell, type TabRequest } from '../lib/shell'
import { isSheet, pageOf, pagesLeft } from '../lib/keys'
import type { Me } from '../api/types'
import type { BoardStore } from '../store/store'
import type { TabScope } from './context'
import { MAX_TABS, safePath, type SavedTabs, type WorkspaceCommand, type WorkspaceState } from './protocol'
import { projectStore } from './projectStore'
import { DraftRegistry } from './drafts'
import { RecoveryCheckpoints } from './checkpoint'
import type { WorkspaceRecovery } from './protocol'
import { MAX_DRAFTS, MAX_RECOVERY_BYTES, SETTINGS_BACKGROUND, settingsBackground } from './protocol'
import { followedLink, page, placement, tabAnchor } from './placement'
import { readOldLink } from '../links/oldLinks'
import { boardForOld, movedProjectPath, projectForPath, readProjectPath } from '../projects/routing'
import { settingsPath } from '../settings/routing'

/** Whose workspace a recovery belongs to: drafts go back only to the same person in the same workspace. */
export const recoveryIdentity = (me: Me) => JSON.stringify([me.workspace.id, me.member?.id ?? null])
/** What App.tsx draws under a sheet opened from `path`: over itself, the sheet's starts on the Board (`/`, which App.tsx draws as it). */
const settingsRedirect = (path: string) => ['/runners', '/new-project'].includes(page(path))
const underOf = (path: string) => isSheet(page(path)) || settingsRedirect(path) ? '/' : path

export type TabRouter = ReturnType<typeof createMemoryRouter>
export interface WorkspaceTab {
  id: string
  router: TabRouter
  store: BoardStore
  scope: TabScope
  path: string
  /** The page drawn under Settings while the sheet is open, or under one it would open (App.tsx keeps the same). */
  under: string
  title: string
  blank: boolean
  /** Each unsaved edit and the page that owns it. */
  dirty: Map<symbol, string>
  titles: Map<symbol, string | null | undefined>
  entries: string[]
  locations: Map<string, string>
  position: number
  children: string[]
  reload: number
  off(): void
  focus: HTMLElement | null
  selection: { start: number; end: number; direction: 'forward' | 'backward' | 'none' } | null
  editorFocus: number
}
export interface WorkspacePane { id: string; tabs: string[]; front: string }
let serial = 0
export class WorkspaceModel {
  readonly tabs = new Map<string, WorkspaceTab>()
  panes: WorkspacePane[] = []
  inUse = 0
  share: number | null = null
  context = 0
  dragged: string | null = null
  private revision = 0
  private readonly drafts = new DraftRegistry()
  private readonly checkpoints = new RecoveryCheckpoints()
  private identity: string | null = null
  private offIdentity: () => void
  private readonly listeners = new Set<() => void>()
  private metadataPending = false
  /**
   * An old address of Needs you or the queue, saved or linked: the Board,
   * under Ready to merge with the proposal it names (App.tsx redirects the
   * same). A saved or recovered tab on `/` (`home`) is the Board too; a new
   * one there follows App.tsx's redirect. Today's link paths (an issue's id, a
   * plan's `/doc/<id>`, a request's `/r/<id>`) lead where they lead now, so a
   * link opened in a tab finds the tab that already shows the page.
   */
  private fromOld(path: string, home = false, selected = this.shared.getState().selectedProjectId): string {
    const state = this.shared.getState()
    // Startup has no snapshot yet: keep the project and proposal until they can be resolved.
    if (state.status !== 'ready') return path
    const location = new URL(path, 'http://workspace.invalid')
    const board = boardForOld(location.pathname, location.hash, [...state.projects.values()], selected, home, [...state.proposals.values()])
    if (board) return board
    // Today's other link paths that lead elsewhere now (links/oldLinks.ts); the pages that only say where a thing went stay as they are.
    const old = readOldLink(location.pathname, location.search, location.hash, { ...state, selectedProjectId: selected })
    return old?.kind === 'redirect' ? old.to : path
  }
  private projectAddress(path: string, selected = this.shared.getState().selectedProjectId): string {
    const location = new URL(path, 'http://workspace.invalid')
    return movedProjectPath(location.pathname, location.search, location.hash,
      [...this.shared.getState().projects.values()], selected) ?? path
  }
  private projectTitle(path: string): string | null {
    const { projects } = this.shared.getState()
    const pathname = page(path)
    const project = projectForPath(pathname, [...projects.values()])
    const kind = readProjectPath(pathname)?.page.split('/')[1]
    const title = ({ board: 'Board', code: 'Code', plans: 'Plans', docs: 'Docs' } as Record<string, string>)[kind ?? '']
    return project && title ? `${title}${projects.size > 1 ? ` · ${project.name}` : ''}` : null
  }
  private metadataChanged() {
    if (this.metadataPending) return
    this.metadataPending = true
    queueMicrotask(() => { this.metadataPending = false; this.changed(false) })
  }
  constructor(readonly shared: BoardStore, private readonly makeRouter: (path: string) => TabRouter, saved: SavedTabs | null, initial = '/', private candidate: WorkspaceRecovery | null = null) {
    if (saved) {
      this.panes = saved.panes.map(pane => {
        const tabs = pane.tabs.map(path => this.makeTab(this.fromOld(path, true), false).id)
        return { id: `pane-${++serial}`, tabs, front: tabs[pane.front]! }
      })
      this.inUse = saved.inUse
      this.share = saved.panes.length === 2 ? saved.share ?? .5 : null
      if (this.tabs.size === 1 && initial === '/' && saved.panes[0]!.tabs[0] === '/') this.current!.blank = true
    } else {
      const tab = this.makeTab(safePath(initial) ?? '/', initial === '/')
      this.panes = [{ id: `pane-${++serial}`, tabs: [tab.id], front: tab.id }]
    }
    this.offIdentity = shared.subscribe(this.identify)
    this.identify()
  }
  private identify = () => {
    const { me, status } = this.shared.getState()
    if (!me || status !== 'ready') return
    const identity = recoveryIdentity(me)
    for (const tab of this.tabs.values()) {
      // An old address kept from startup (Needs you, the queue) leads to its Board now that projects are known; any other is left as it is.
      const selected = tab.store.getState().selectedProjectId
      const board = this.fromOld(tab.path, true, selected)
      if (board !== tab.path) void tab.router.navigate(this.projectAddress(board, selected), { replace: true })
      if (tab.title !== 'Poulpe') continue
      const title = this.projectTitle(tab.path)
      if (title) { tab.title = title; this.metadataChanged() }
    }
    if (this.identity === identity) return
    const first = this.identity === null
    this.identity = identity
    const candidate = this.candidate
    this.candidate = null
    if (first && candidate?.identity === identity) {
      for (const tab of this.tabs.values()) { tab.off(); tab.router.dispose() }
      this.tabs.clear()
      const under = new Map<string, string>()
      for (const draft of candidate.drafts) {
        const path = draft.key === SETTINGS_BACKGROUND && !under.has(draft.tab) ? settingsBackground(draft.value) : null
        if (path) under.set(draft.tab, path)
      }
      this.panes = candidate.state.panes.map(pane => ({ id: `pane-${++serial}`, front: pane.front, tabs: pane.tabs.map(held => {
        // A tab recovered under Settings reopens on the page that was under the sheet.
        const tab = this.makeTab(this.projectAddress(this.fromOld((isSheet(page(held.path)) && under.get(held.id)) || held.path, true)), held.blank, undefined, held.id)
        tab.title = held.title
        return tab.id
      }) }))
      this.inUse = candidate.state.inUse
      this.share = candidate.state.panes.length === 2 ? candidate.state.share ?? .5 : null
      this.context = candidate.state.context + 1
      // The registry refuses the reserved metadata key: only editors' drafts are restored.
      for (const draft of candidate.drafts) {
        const project = this.tabs.get(draft.tab)?.store.getState().selectedProjectId
        this.drafts.write(draft.tab, this.projectAddress(draft.path, project), draft.key, draft.value, draft.dirty)
      }
      this.changed(false)
    } else {
      this.clearRecovery()
      if (!first) {
        // Replace every account-scoped view and metadata before publishing the
        // new identity. Suspended descendants cannot be trusted to clean up first.
        for (const tab of this.tabs.values()) { tab.off(); tab.router.dispose() }
        this.tabs.clear()
        const tab = this.makeTab('/', true)
        this.panes = [{ id: `pane-${++serial}`, tabs: [tab.id], front: tab.id }]
        this.inUse = 0
        this.share = null
        this.dragged = null
        this.changed()
      }
    }
  }
  clearRecovery() { this.checkpoints.cancel(); this.drafts.clear(); tellShell({ workspaceRecovery: null }) }
  recovery(): WorkspaceRecovery | null {
    if (!this.identity || !this.drafts.bounded) return null
    // The state stays the live one: the shell accepts a checkpoint only when
    // it is. A tab with Settings open says the page under it in a reserved
    // non-dirty draft, and recovers there with the sheet closed.
    const drafts = this.drafts.snapshot()
    for (const tab of this.tabs.values()) {
      if (isSheet(page(tab.path))) drafts.push({ tab: tab.id, path: page(tab.path), key: SETTINGS_BACKGROUND, value: JSON.stringify(tab.under), dirty: false })
    }
    if (drafts.length > MAX_DRAFTS) return null
    const payload: WorkspaceRecovery = { version: 1, identity: this.identity, state: this.state(), drafts }
    return new TextEncoder().encode(JSON.stringify(payload)).length <= MAX_RECOVERY_BYTES ? payload : null
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener) } }
  snapshot = () => this.revision
  get current() { return this.tabs.get(this.panes[this.inUse]?.front ?? '') }
  private changed(context = true) {
    if (context) this.context += 1
    this.revision += 1
    for (const listener of this.listeners) listener()
  }
  private makeTab(address: string, blank: boolean, source?: WorkspaceTab, restoredId?: string) {
    const path = this.fromOld(address, false, source?.store.getState().selectedProjectId)
    let id = restoredId ?? `tab-${++serial}`
    while (!restoredId && this.tabs.has(id)) id = `tab-${++serial}`
    const router = this.makeRouter(path)
    const tab: WorkspaceTab = {
      id, router, store: projectStore(this.shared, source?.store.getState().selectedProjectId, () => router.state.location.pathname), path, under: underOf(path), title: this.projectTitle(path) ?? 'Poulpe', blank,
      dirty: new Map(), titles: new Map(), entries: [router.state.location.key], locations: new Map([[router.state.location.key, path]]), position: 0, children: [], reload: 0, focus: null, selection: null, editorFocus: 0, off: () => {},
      scope: {
        id, ownsInteraction: () => this.current === tab,
        title: (token, title) => {
          if (title === undefined) tab.titles.delete(token)
          else tab.titles.set(token, title)
          const next = [...tab.titles.values()].at(-1) || 'Poulpe'
          if (tab.title === next) return
          tab.title = next.slice(0, 512)
          this.metadataChanged()
        },
        dirty: (token, dirty, owner = pageOf(page(tab.path))) => {
          const was = tab.dirty.size > 0
          if (dirty) { tab.dirty.set(token, owner); tab.blank = false } else tab.dirty.delete(token)
          if (was !== (tab.dirty.size > 0)) this.metadataChanged()
        },
        hasUnsaved: pages => this.isDirty(tab, pages),
        surface: () => document.querySelector<HTMLElement>(`[data-workspace-tab="${id}"]`),
        path: () => tab.path,
        background: () => tab.under,
        navigate: (path, replace = false) => { void router.navigate(path, { replace }) },
        readDraft: (path, key) => this.drafts.read(id, path, key),
        writeDraft: (path, key, value, dirty) => { if (this.tabs.get(id) === tab && this.drafts.write(id, path, key, value, dirty)) this.metadataChanged() },
        checkpoint: () => this.tabs.get(id) === tab ? this.checkpoints.save(id, this.recovery()) : Promise.resolve(false),
        reload: () => this.reload(tab),
      },
    }
    tab.off = router.subscribe(state => {
      const location = state.location
      const next = safePath(location.pathname + location.search + location.hash)
      if (!next || next === tab.path && tab.entries[tab.position] === location.key) return
      // Hidden tabs may traverse several entries before React resumes them.
      // Remember every addressed project before an unqualified sheet/entity follows it.
      tab.store.getState()
      // Router publication means any mounted blocker has accepted the move.
      // A hidden tab's guard runs in open/history before navigation. Discard
      // only the page left, including recovered editors not yet mounted:
      // Settings opening over a page, or closing back to it, leaves it whole.
      const below = pageOf(page(isSheet(page(tab.path)) ? tab.under : tab.path))
      const left = pagesLeft(page(tab.path), page(next), page(tab.under))
      if (left.includes(below)) this.checkpoints.cancel(id)
      for (const leaving of left) {
        this.drafts.removePath(id, leaving)
        for (const [token, owner] of tab.dirty) if (owner === leaving) tab.dirty.delete(token)
      }
      if (!isSheet(page(next)) && !settingsRedirect(next)) tab.under = underOf(next)
      tab.locations.set(location.key, next)
      const at = tab.entries.indexOf(location.key)
      if (state.historyAction === 'POP' && at >= 0) tab.position = at
      else if (state.historyAction === 'REPLACE') {
        const previous = tab.entries[tab.position]
        if (previous && previous !== location.key) tab.locations.delete(previous)
        tab.entries[tab.position] = location.key
      } else {
        for (const removed of tab.entries.splice(tab.position + 1)) tab.locations.delete(removed)
        tab.entries.push(location.key); tab.position += 1
      }
      tab.path = next
      const title = this.projectTitle(next)
      if (title) tab.title = title
      tab.blank = false
      this.changed()
    })
    this.tabs.set(id, tab)
    return tab
  }
  paneOf(id: string) { return this.panes.findIndex(pane => pane.tabs.includes(id)) }
  used(id: string) {
    const tab = this.tabs.get(id)
    // Deliberate persistent-workspace difference from WK didFinish: loading
    // chrome does not consume the unused initial tab before launch links arrive.
    if (tab?.blank) { tab.blank = false; this.changed(false) }
  }
  usePane(index: number) {
    if (index !== this.inUse && this.panes[index]) { this.rememberFocus(); this.inUse = index; this.changed() }
  }
  rememberFocus(element: Element | null = document.activeElement) {
    const current = this.current
    if (current && element instanceof HTMLElement && element.closest('[data-workspace-tab]')?.getAttribute('data-workspace-tab') === current.id) {
      current.focus = element
      current.editorFocus = [...(current.scope.surface()?.querySelectorAll('.cm-content') ?? [])].indexOf(element)
      current.selection = (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) && element.selectionStart !== null
        ? { start: element.selectionStart, end: element.selectionEnd!, direction: element.selectionDirection ?? 'none' } : null
    }
  }
  select(id: string) {
    const pane = this.paneOf(id)
    if (pane < 0 || this.current?.id === id) return
    this.rememberFocus()
    this.panes[pane]!.front = id
    this.inUse = pane
    this.changed()
  }
  private isDirty(tab: WorkspaceTab, pages?: readonly string[]) {
    return [...tab.dirty.values()].some(owner => !pages || pages.includes(owner)) || this.drafts.hasDirty(tab.id, pages)
  }
  private allowed(tab: WorkspaceTab, pages?: readonly string[]) { return !this.isDirty(tab, pages) || window.confirm(DISCARD_QUESTION) }
  /** What a hidden tab's move to `path` throws away; its useBlocker is suspended. */
  private leaves(tab: WorkspaceTab, path: string) { return pagesLeft(page(tab.path), page(path), page(tab.under)) }
  close(id: string) {
    const tab = this.tabs.get(id)
    if (!tab || !this.allowed(tab)) return
    if (this.tabs.size === 1) { tellShell({ workspaceCloseWindow: true }); return }
    this.rememberFocus()
    this.removeFromPane(id)
    this.checkpoints.cancel(id)
    tab.off(); tab.router.dispose(); this.tabs.delete(id); this.drafts.removeTab(id)
    this.changed()
  }
  private removeFromPane(id: string) {
    const index = this.paneOf(id)
    const pane = this.panes[index]!
    const at = pane.tabs.indexOf(id)
    pane.tabs.splice(at, 1)
    if (!pane.tabs.length) {
      this.panes.splice(index, 1)
      if (this.inUse >= index) this.inUse = Math.max(0, this.inUse - 1)
      this.share = null
    } else if (pane.front === id) pane.front = pane.tabs[Math.min(at, pane.tabs.length - 1)]!
  }
  open(path: string, disposition: Extract<WorkspaceCommand, { type: 'open' }>['disposition'], source?: string) {
    const input = safePath(path)
    const from = source === undefined ? this.current : this.tabs.get(source)
    if (!input || !from) return
    // A background tab may never mount before quit: qualify old links now,
    // while their opener's project is still known, before publishing saved paths.
    const selected = from.store.getState().selectedProjectId
    const safe = this.projectAddress(this.fromOld(input, false, selected), selected)
    if (disposition === 'current') {
      // A hidden router has suspended its useBlocker effect; its dirty registry remains authoritative.
      const hidden = this.panes[this.paneOf(from.id)]?.front !== from.id
      const left = this.leaves(from, safe)
      if (hidden && left.length && !this.allowed(from, left)) return
      void from.router.navigate(safe); return
    }
    if (disposition === 'external') {
      const all = this.panes.flatMap(pane => pane.tabs.map(id => this.tabs.get(id)!))
      const known = all.map(tab => ({ page: tab.path, blank: tab.blank, unsaved: this.isDirty(tab) }))
      const result = source === undefined ? placement(safe, known) : followedLink(safe, all.indexOf(from), known)
      // A followed anchor in its own tab navigates without changing pane ownership.
      if (result.type === 'here') { void from.router.navigate(safe); return }
      if (result.type !== 'new') {
        const existing = all[result.index]!
        if (result.type === 'take') void existing.router.navigate(safe)
        this.select(existing.id)
        return
      }
    }
    const fromPane = this.paneOf(from.id)
    if (disposition === 'other-pane' && this.panes.length === 1) {
      const tab = this.makeTab(safe, false, from)
      this.panes.push({ id: `pane-${++serial}`, tabs: [tab.id], front: tab.id })
      this.share = .5
      this.changed()
      return
    }
    const target = disposition === 'other-pane' ? 1 - fromPane : fromPane
    const pane = this.panes[target]!
    if (pane.tabs.length >= MAX_TABS) return
    if (disposition !== 'background' && disposition !== 'other-pane') this.rememberFocus()
    const tab = this.makeTab(safe, false, from)
    const grouped = disposition !== 'other-pane' && !(disposition === 'external' && source === undefined)
    const anchor = grouped ? tabAnchor(from.id, from.children, pane.tabs) : null
    pane.tabs.splice(anchor === null ? pane.tabs.length : anchor + 1, 0, tab.id)
    if (grouped) from.children.push(tab.id)
    if (disposition !== 'background') pane.front = tab.id
    if (disposition !== 'background' && disposition !== 'other-pane') this.inUse = target
    this.changed()
  }
  move(id: string, target: number, before: string | null = null) {
    const from = this.paneOf(id)
    if (from < 0 || target < 0 || target > 1 || before === id || (target !== from && this.tabs.size === 1)) return
    if (target !== from && (this.panes[target]?.tabs.length ?? 0) >= MAX_TABS) return
    if (before !== null && !this.panes[target]?.tabs.includes(before)) return
    this.rememberFocus()
    // Keep the destination object when removing an empty source shifts pane indices.
    const destination = this.panes[target] ?? { id: `pane-${++serial}`, tabs: [], front: id }
    if (target === from) {
      destination.tabs.splice(destination.tabs.indexOf(id), 1)
    } else this.removeFromPane(id)
    if (!this.panes.includes(destination)) this.panes.push(destination)
    destination.tabs.splice(before === null ? destination.tabs.length : destination.tabs.indexOf(before), 0, id)
    if (from !== target) {
      destination.front = id
      this.inUse = this.panes.indexOf(destination)
    }
    this.share = this.panes.length === 2 ? this.share ?? .5 : null
    this.changed()
  }
  resize(share: number, width = 0) {
    if (this.panes.length !== 2 || !Number.isFinite(share)) return
    // Existing SplitView rules: traffic lights need 480 px on the left;
    // the trailing pane keeps the web UI's 375 px minimum.
    const low = width >= 855 ? Math.max(.1, 480 / width) : .1
    const high = width >= 855 ? Math.min(.9, 1 - 375 / width) : .9
    const next = Math.min(high, Math.max(low, Math.round(share * 1000) / 1000))
    if (next === this.share) return
    this.share = next
    this.changed(false)
  }
  private history(tab: WorkspaceTab, step: -1 | 1) {
    const path = tab.locations.get(tab.entries[tab.position + step] ?? '')
    if (!path) return
    const left = this.leaves(tab, path)
    if (this.panes[this.paneOf(tab.id)]?.front !== tab.id && left.length && !this.allowed(tab, left)) return
    void tab.router.navigate(step)
  }
  private reload(tab: WorkspaceTab) { if (this.allowed(tab)) { this.drafts.removeTab(tab.id); tab.dirty.clear(); tab.reload += 1; this.changed() } }
  dispatch(value: WorkspaceCommand) {
    const current = this.current
    if (!current) return
    const target = 'source' in value && value.source !== undefined ? this.tabs.get(value.source) : current
    if (!target) return
    switch (value.type) {
      case 'open': this.open(value.path, value.disposition, value.source); break
      case 'new-tab': {
        const pane = this.panes[this.inUse]!
        if (pane.tabs.length >= MAX_TABS) break
        this.rememberFocus()
        const tab = this.makeTab('/', false, current)
        pane.tabs.push(tab.id); pane.front = tab.id; this.changed(); break
      }
      case 'close-tab': this.close(target.id); break
      case 'cycle': {
        const pane = this.panes[this.inUse]!
        const at = pane.tabs.indexOf(pane.front)
        this.select(pane.tabs[((at + value.step) % pane.tabs.length + pane.tabs.length) % pane.tabs.length]!); break
      }
      case 'split': {
        if (this.panes.length === 1) this.open(current.path, 'other-pane')
        else {
          const ids = [...this.panes[1 - this.inUse]!.tabs]
          if (ids.some(id => this.isDirty(this.tabs.get(id)!)) && !window.confirm(DISCARD_QUESTION)) break
          for (const id of ids) { this.checkpoints.cancel(id); const tab = this.tabs.get(id)!; this.removeFromPane(id); tab.off(); tab.router.dispose(); this.tabs.delete(id); this.drafts.removeTab(id) }
          this.changed()
        }
        break
      }
      case 'move-other': this.move(value.tab ?? current.id, 1 - this.paneOf(value.tab ?? current.id)); break
      case 'back': this.history(target, -1); break
      case 'forward': this.history(target, 1); break
      case 'reload': this.reload(target); break
      case 'settings': {
        if (isSheet(page(target.path))) break
        const { selectedProjectId } = target.store.getState()
        this.open(selectedProjectId ? settingsPath('project', 'repository', selectedProjectId) : settingsPath('workspace', 'projects'), 'current', target.id)
        break
      }
    }
  }
  tabRequest(pane: number, request: TabRequest) {
    if ('select' in request) this.select(request.select)
    else if ('close' in request) this.close(request.close)
    else if ('new' in request) { this.usePane(pane); this.dispatch({ type: 'new-tab' }) }
    else if ('drag' in request) { this.dragged = request.drag; this.changed(false) }
    else if ('drop' in request && this.dragged) { this.move(this.dragged, pane, request.drop.before); this.dragged = null; this.changed(false) }
  }
  state(): WorkspaceState {
    return { version: 1, context: this.context, inUse: this.inUse, share: this.share,
      panes: this.panes.map(pane => ({ front: pane.front, tabs: pane.tabs.map(id => {
        const tab = this.tabs.get(id)!
        return { id, path: tab.path, title: tab.title, blank: tab.blank, unsaved: this.isDirty(tab), canGoBack: tab.position > 0, canGoForward: tab.position + 1 < tab.entries.length }
      }) })) }
  }
  dispose() { this.checkpoints.dispose(); this.offIdentity(); for (const tab of this.tabs.values()) { tab.off(); tab.router.dispose() }; this.listeners.clear() }
}
